import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

type UsePaginationOptions = {
    initialPage?: number;
    initialPageSize?: number;
    syncWithUrl?: boolean;
};

export function usePagination(options: UsePaginationOptions = {}) {
    const { initialPage = 1, initialPageSize = 10, syncWithUrl = false } = options;

    const [searchParams, setSearchParams] = useSearchParams();

    const getInitialPage = () => {
        if (syncWithUrl) {
            const urlPage = searchParams.get("page");
            return urlPage ? Number(urlPage) : initialPage;
        }
        return initialPage;
    };

    const getInitialPageSize = () => {
        if (syncWithUrl) {
            const urlPageSize = searchParams.get("pageSize");
            return urlPageSize ? Number(urlPageSize) : initialPageSize;
        }
        return initialPageSize;
    };

    const [page, setPageState] = useState(getInitialPage);
    const [pageSize, setPageSizeState] = useState(getInitialPageSize);

    const setPage = useCallback((newPage: number) => {
        setPageState(newPage);
    }, []);

    const setPageSize = useCallback((newSize: number) => {
        setPageSizeState(newSize);
    }, []);

    const handlePageSizeChange = useCallback((current: number, size: number) => {
        setPageState(current);
        setPageSizeState(size);
    }, []);

    const resetPage = useCallback(() => {
        setPageState(1);
        setPageSizeState(initialPageSize);
    }, [initialPageSize]);

    const getRowNumber = useCallback((index: number) => (page - 1) * pageSize + index + 1, [page, pageSize]);

    // URL 파라미터 동기화
    useEffect(() => {
        if (syncWithUrl) {
            searchParams.set("page", String(page));
            searchParams.set("pageSize", String(pageSize));
            setSearchParams(searchParams);
        }
    }, [page, pageSize, syncWithUrl]);

    return {
        page,
        pageSize,
        setPage,
        setPageSize,
        handlePageSizeChange,
        resetPage,
        getRowNumber,
        searchParams,
        setSearchParams,
    };
}
